import { useEffect, ReactElement, useMemo } from "react";
import { GrowthBook, GrowthBookProvider } from "@growthbook/growthbook-react";
import { BrowserCookieStickyBucketService } from "@growthbook/growthbook";
import {
    getCookiesValueByName,
    setCookie,
} from "@smile2impress/attraction-utils";
import { nanoid } from "nanoid";
import Cookies from "js-cookie";

import { COOKIES, YearInSeconds } from "constants/enum";
import { sendGTMEvent } from "services/AnaliticsService/sendGoogleAnalitic";
import { GTMEvents } from "services/AnaliticsService";
import { getAppMode } from "utils/getAppMode";

import { saveExpToCookies } from "./utils/saveExpToCookies";

export interface ExperimentProviderProps {
    fullPath: string;
    children: ReactElement;

    route?: string;
    locale?: string;
}

const { isLiveMode } = getAppMode();
let growthbook: GrowthBook | null = null;

function getGrowthBook() {
    if (!growthbook) {
        growthbook = new GrowthBook({
            debug: !isLiveMode,
            enableDevMode: !isLiveMode,
            apiHost: process.env.GROWTHBOOK_API_HOST,
            clientKey: process.env.GROWTHBOOK_CLIENT_KEY,
            trackingCallback: (experiment: any, result: any) => {
                const UUID =
                    window?.impressUUID || getCookiesValueByName(COOKIES.UUID);
                sendGTMEvent(GTMEvents.GROWTHBOOK_EXPERIMENT_VIEWED, {
                    experiment_id: experiment.key,
                    variation_id: result.key,
                    ...(UUID ? { user_id: UUID } : {}),
                });
                saveExpToCookies(experiment.key, result.key);
            },
            stickyBucketService: new BrowserCookieStickyBucketService({
                jsCookie: Cookies,
            }),
            subscribeToChanges: true,
        });
        void growthbook.init({ streaming: true });
    }
    return growthbook;
}

export const ExperimentProvider = ({
    locale = "es",
    fullPath,
    children,
}: ExperimentProviderProps) => {
    const gb = useMemo(() => {
        if (typeof window === "undefined") {
            // SSR stub: no network, no SSE, discarded after render
            return new GrowthBook({
                apiHost: process.env.GROWTHBOOK_API_HOST,
                clientKey: process.env.GROWTHBOOK_CLIENT_KEY,
            });
        }
        return getGrowthBook();
    }, []);

    useEffect(() => {
        const path = `/${locale}${fullPath}`;
        const { path: referrer } = gb.getAttributes();
        gb.updateAttributes({ path, referrer });
    }, [fullPath, locale]);

    useEffect(() => {
        const path = `/${locale}${fullPath}`;
        let UUID;

        const setUUIDCookie = (newUUID: string) =>
            setCookie(COOKIES.UUID, newUUID, {
                "max-age": 10 * YearInSeconds,
                domain: ".smile2impress.com",
                path: "/",
            });

        if (window !== undefined) {
            UUID = window?.impressUUID;
            if (UUID) setUUIDCookie(UUID);
        }

        if (!UUID) UUID = getCookiesValueByName(COOKIES.UUID);

        if (!UUID) {
            UUID = nanoid();
            setUUIDCookie(UUID);
        }

        gb.updateAttributes({
            id: UUID,
            user_id: UUID,
            referrer: "",
            path,
        });
    }, []);

    return <GrowthBookProvider growthbook={gb}>{children}</GrowthBookProvider>;
};
